import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

interface CostValue {
  amount: number | null;
  currency: string;
}

@Component({
  selector: 'app-cost-currency-input',
  templateUrl: './cost-currency-input.component.html',
  styleUrls: ['./cost-currency-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CostCurrencyInputComponent),
      multi: true,
    },
  ],
})
export class CostCurrencyInputComponent implements ControlValueAccessor {
  @Input() currencies: string[] = [
    'INR', 'USD', 'EUR', 'GBP', 'AUD', 'CAD', 'JPY', 'CNY', 'SGD', 'NZD',
    'CHF', 'SEK', 'NOK', 'DKK', 'KRW', 'BRL', 'MXN', 'ZAR', 'TRY', 'THB',
    'IDR', 'MYR', 'PHP', 'PLN', 'RUB', 'AED', 'SAR', 'KWD', 'OMR', 'EGP'
  ];

  @Output() valueChange = new EventEmitter<CostValue>();

  /** Always initialize with safe defaults */
  @Input() value: CostValue = { amount: null, currency: 'INR' };

  onChange: any = () => {};
  onTouched: any = () => {};
  disabled = false;

  /** Ensure value is never null */
  private ensureValue(obj?: CostValue | null): CostValue {
    return obj && typeof obj === 'object'
      ? {
          amount: obj.amount ?? null,
          currency: obj.currency ?? 'INR',
        }
      : { amount: null, currency: 'INR' };
  }

  writeValue(obj: CostValue | null): void {
    this.value = this.ensureValue(obj);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  updateAmountOnInput(event: Event) {
    const input = event.target as HTMLInputElement;
    this.value = this.ensureValue(this.value); // safeguard
    this.value.amount = input.value ? +input.value : null;
  }

  onAmountBlur() {
    this.emitValue(); // emit only when leaving field
  }

  updateCurrency(eventOrValue: Event | string) {
    this.value = this.ensureValue(this.value); // safeguard

    if (typeof eventOrValue === 'string') {
      this.value.currency = eventOrValue;
    } else {
      const select = eventOrValue.target as HTMLSelectElement;
      this.value.currency = select.value;
    }

    this.emitValue();
  }

  private emitValue() {
    this.value = this.ensureValue(this.value); // final safeguard
    this.onChange(this.value);   // reactive form use
    this.valueChange.emit(this.value); // template use
  }
}
